import { useApiProxy } from '@/utils/use.api.proxy'
import { GoogleOutlined } from '@ant-design/icons'
import { captureException } from '@tucan/utils/error-tracking'
import { getPublicEnv } from '@tucan/utils'
import { App, Button, Space, Tooltip } from 'antd'
import debug from 'debug'
import React, { FC, ReactElement, useCallback } from 'react'
import GoogleLogin, { GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login'

const logger = debug('LoginWithGoogleButton')

interface Props {
  onSubmitting?: (submitting: boolean) => void
  onSuccess?: () => void
  disabled?: boolean
  onClick?: (cb: () => void) => void
}

export const LoginWithGoogleButton: FC<Props> = (props) => {
  const [errorTitle, setErrorTitle] = React.useState<string>()
  const { trigger } = useApiProxy('/auth/google')
  const { message } = App.useApp()

  const onSuccess = useCallback(
    async (params: GoogleLoginResponse | GoogleLoginResponseOffline): Promise<void> => {
      try {
        props.onSubmitting?.(true)

        const result = await trigger({
          data: {
            code: params.code,
          },
          method: 'POST',
        })

        if (result.login) {
          void message.success('You are now logged in!')
          props.onSuccess?.()
        } else {
          logger('bad login result %O', result)
          void message.error('Login with google failed')
        }
      } catch (e) {
        logger('login failed %O', e)
        void message.error('Login with google failed')
        captureException(e)
      }

      props.onSubmitting?.(false)
    },
    [message, props, trigger]
  )

  const wrap = (element: ReactElement): ReactElement => {
    if (errorTitle) {
      return <Tooltip title={errorTitle}>{element}</Tooltip>
    }

    return element
  }

  if (!getPublicEnv('GOOGLE_CLIENT_ID')) {
    return null
  }

  return (
    <GoogleLogin
      clientId={getPublicEnv('GOOGLE_CLIENT_ID')}
      responseType='code'
      accessType='offline'
      render={(renderProps) =>
        wrap(
          <Button
            size="large"
            block
            onClick={props.onClick ? () => props.onClick(renderProps.onClick) : renderProps.onClick}
            disabled={props.disabled || renderProps.disabled}
          >
            <Space>
              <GoogleOutlined />
              <span>Login with Google</span>
            </Space>
          </Button>
        )
      }
      buttonText={'Login'}
      onSuccess={(data) => void onSuccess(data)}
      disabled={Boolean(errorTitle)}
      onFailure={(e) => {
        if (e.error === 'popup_closed_by_user') {
          return
        }

        logger('callback failure %O', e)

        if (e.error === 'idpiframe_initialization_failed') {
          setErrorTitle(e.details)
          return
        }

        captureException(e)
        void message.warning({
          content: e.details || 'Cannot use Google Login',
        })
      }}
      cookiePolicy={'single_host_origin'}
    />
  )
}
